import { useState, type FormEvent, type ReactNode } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Input } from './Input';

/** The fewest characters a staff password may have (the server's rule, 07 §7.1). */
export const MIN_PASSWORD_LENGTH = 10;

export interface ChoosePasswordFormProps {
  /** Resolve on success; throw an Error whose message is shown to the person. */
  onSubmit: (password: string) => Promise<void>;
  size?: 'md' | 'lg' | 'touch';
  footer?: ReactNode;
}

/**
 * Shown after signing in with a temporary password (an agent or a manager set it): the person
 * chooses their own before anything else opens (07 §7.2).
 */
export function ChoosePasswordForm({ onSubmit, size = 'lg', footer }: ChoosePasswordFormProps) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== again) {
      setError('The two passwords are not the same.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The password was not changed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      noValidate
      style={{ display: 'grid', gap: 'var(--space-4)', width: '100%' }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>Choose your password</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--fg-muted)' }}>
          You signed in with a temporary password. Choose your own to carry on.
        </p>
      </div>
      {error ? (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3)',
            borderRadius: 'var(--r-md)',
            background: 'var(--bad-bg)',
            color: 'var(--bad)',
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      ) : null}
      <Field label="New password" hint={`At least ${MIN_PASSWORD_LENGTH} characters`} required>
        {(p) => (
          <Input
            {...p}
            size={size}
            name="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <Field label="New password again" required>
        {(p) => (
          <Input
            {...p}
            size={size}
            name="new-password-again"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        )}
      </Field>
      <Button
        type="submit"
        variant="cta"
        size={size === 'touch' ? 'touch' : 'lg'}
        block
        loading={busy}
      >
        Save my password
      </Button>
      {footer}
    </form>
  );
}
